import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { EzApi } from '/public/common/javascript/ezapi.js'
        import { EzUI } from '/public/common/javascript/ezui.js'
        import { ezUI } from '/public/common/javascript/ezui.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        WARNING: Do not import the following helper classes into this class to avoid
        circular dependencies:
        import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
        import { EzChar } from '/ezlibrary/helpers/EzChar.js';
        import { EzString } from '/ezlibrary/helpers/EzString.js';
        import { EzStringConstants } from '/ezlibrary/helpers/EzStringConstants.js';
        import { EzArray } from '/ezlibrary/helpers/EzArray.js';
        import { EzMap } from '/ezlibrary/helpers/EzMap.js';
        import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMIPORTANT: Verify all existing and new Jest Javascript unit tests located at
    {project_root}/src/main/webapp/tests/ezlibrary/EzNumber.test.js pass before
    commiting changes to EzNumber.js. You can run the Jest unit tests from the
    command line by following the instructions below:
        1) Open your bash terminal and navigate to the {project_root}/src/main/webapp folder.
        2) Execute the following command in the {project_root}/src/main/webapp folder:
           npx jest ./tests/ezlibrary/helpers/EzNumber.test.js
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Recommend using the following AI prompt to easily update, correct, and add Jest
    unit tests for EzNumber.js:
    -------------------------------------------------------------------------------
    # Update, Fix and Add Additional Unit Tests for EzNumber.js
    As a expert Javascript engineer with extensive experience using the Jest
    framework for writing Javascript unit tests, perform the following steps:
    ## Step 1: Review and Understand Implementation
    Review the Javascript class EzNumber.js located at
    src\main\webapp\ezlibrary\helpers\EzNumber.js and make note
    of any suggestions to improve the EzNumber.js classes implementation.
    Review the Jest unit tests in EzNumber.test.js Javascript file located at
    src/main/webapp/tests/ezlibrary/helpers/EzNumber.test.js and make note of any
    suggestions to improve the test implementation and any existing gaps in test
    coverage that you identify.
    - Once your review is complete and you are ready to work, continue to Step 2.
    ## Step 2: Update Existing Unit Tests
    Report to the user what unit tests will need updated to account for any changes
    that were made to the EzNumber.js Javascript class that impact the existing
    tests (if any at all).
    - Ask they user if they would like you to continue and apply your proposed changes.
        - If they user agrees, apply the proposed changes to the existing unit tests.
        - If the user disagrees, then you can stop executing these steps and report
        to the user that you will not be applying any changes to the existing unit tests.
    - If you didn't need to apply changes OR you have applied the changes to existing
    unit tests, then you can continue to Step 3.
    ## Step 3: Verify Existing Unit Tests Pass
    Run the existing unit tests and verify all the tests pass.
    - If any of the unit tests fail, then report your proposed fixes for the faillures
    to the user and then ask the user which of your proposed fixes they would like you
    to apply (if any).
    - If the user asks you to apply one or more of your proposed fixes, then apply
    the fix and repeat Step 3 to verify.
    - If all of the existing unit tests pass you can continue to Step 4.
    ## Step 4: Add Additional Unit Tests
    Add additional comprehensive unit tests using the Jest framework for any new,
    modified, or untested areas of the EzNumber.js class.
    - When generating the additional unit tests, follow the below guidelines:
        1. Create unit tests using the Jest framework.
        2. Organize the unit tests by what is being tested.
        3. Use descriptive names for each unit tests
        4. Add and/or update JSDoc documentation for all unit tests and make sure
        the documentation includes the line "Unit Tests Generated by Claude Code"
    - Once you have added the additional unit tests, continue to Step 5.
    ## Step 5: Verify All Unit Test Pass
    Run the unit tests to verify all tests pass.
    - If any of the unit tests fail, then report your proposed fixes for the
    faillures to the user and then ask the user which of your proposed fixes
    they would like you to apply (if any).
    - If the user asks you to apply one or more of your proposed fixes, then
    apply the fix and repeat Step 5 to verify the fixes.
    - Once all the unit tests are passing, you can continue to Step 6.
    ## Step 6
    Generate a summary of the work that you have done so the user can use the sumary
    as their git commit message.
    - Once the summary is generated you can stop executing these steps.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Static class that provides utility methods and properties for numbers
 * Jest Unit Tests: {project_root}/src/main/webapp/tests/ezlibrary/EzNumber.test.js
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * The following additional static read-only properties (getters) get dynamically
 * added to the EzNumber class during static initialization. See the static
 * initialization section at the end of the EzNumber class implementation.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * General Positive Number Constants
 *     1) EzNumber._{aNumber} = {aNumber} for aNumber in the sequence of 0 to 25
 *     2) EzNumber.{numberWords[aNumber - 1]} = EzNumber._{aNumber} for aNumber in the sequence of 0 to 25
 *     3) EzNumber._{otherNumber} = {otherNumber} for otherNumber in the set of [30, 45, 100, 200, 300, 365, 400, 500, 600, 999, 9999]
 *     4) EzNumber._1700, EzNumber._1776, EzNumber._1787, EzNumber._1800, EzNumber._1863, EzNumber._1900, EzNumber._1915, EzNumber._1910, EzNumber._1920,
 *        EzNumber._1929, EzNumber._1930, EzNumber._1940, EzNumber._1941, EzNumber._1945, EzNumber._1950, EzNumber._1960, EzNumber._1968, EzNumber._1969,
 *        EzNumber._1970, EzNumber._1971, EzNumber._1980, EzNumber._1988, EzNumber._1990, EzNumber._2000, EzNumber._2001, EzNumber._2010, EzNumber._2014
 *     5) EzNumber._2015 through EzNumber.{current year}.
 *     6) EzNumber.{otherNumberWords[otherNumberConstants.indexOf(otherNumber)]} = EzNumber._{otherNumber} for otherNumber in the set of
 *        30, 45, 100, 200, 300, 365, 400, 500, 600, 999, 9999
 *     7) EzNumber._{otherLargeNumber} = {otherLargeNumber} for otherLargeNumber in the set of
 *        [1000, (60 * 1000)=60000, (60 * 60 * 1000)=3600000, (24 * 60 * 60 * 1000)=86400000, (7 * 24 * 60 * 60 * 1000)=604800000, 2628000000,
 *        (30 * 24 * 60 * 60 * 1000)=2592000000, 31536000000,(365 * 24 * 60 * 60 * 1000)=31536000000,3600,(24 * 3600)=86400,(7 * 24 * 3600)=604800,2628000,
 *        (30 * 24 * 3600)=2592000,31536000,(365 * 24 * 3600)=31536000,1440,(7 * 1440)=10080,43800,(30 * 1440)=43200,525600,(365 * 1440)=525600,(7 * 24)=168,
 *        (30 * 24)=720,(365 * 24)=8760,30.44,365.25,4.345,52.14]
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * General Negative Number Constants
 *     1) EzNumber._n{Math.abs(negativeNumber)} = {negativeNumber} for negativeNumber in the set of
 *        [-1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13]
 *     2) EzNumber.MINUS_n{Math.abs(negativeNumber)} = EzNumber._n{Math.abs(negativeNumber)} for negativeNumber in the set of
 *        [-1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13]
 *     3) EzNumber.{negativeNumberWords[negativeNumberConstants.indexOf(negativeNumber)]} = EzNumber._n{Math.abs(negativeNumber)} for negativeNumber
 *        in the set of [-1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13]
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Currency Constants
 *     1) EzNumber.CURRENCY_ZERO = 0;
 *     2) EzNumber.sCURRENCY_ZERO = '0.00';
 *     3) EzNumber.ZERO$ = EzNumber.CURRENCY_ZERO;
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * NaN Constants
 *     1) EzNumber.NaN = Number.NaN;
 *     2) EzNumber.NAN = EzNumber.NaN;
 *     3) EzNumber.NaN_AS_STRING = 'NaN';
 *     4) EzNumber.NAN_AS_STRING = EzNumber.NaN_AS_STRING;
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Number as String Constants
 *     1) EzNumber.s{aNumber} = '{aNumber}' for aNumber in the sequence of 0 to 25
 *     2) EzNumber.s{otherNumber} = '{otherNumber}' for otherNumber in the set of 30, 45, 100, 9999
 *     3) EzNumber.sNEG_{negativeNumber} = '{negativeNumber}' for negativeNumber in the set of -1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13
 *     4) EzNumber.sMINUS_{negativeNumber} = '{negativeNumber}' for negativeNumber in the set of -1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13
 *     5) EzNumber.sMINUS_ONE = '-1', EzNumber.sMINUS_TWO = '-2', ..., EzNumber.sMINUS_THIRTEEN = '-13'
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-helper classes with:
 *      import {
 *          // ... other helper class imports
 *          EzNumber
 *      } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other helper classes with:
 *     import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static Access Only:
 *     EzNumber.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzNumber extends EzStaticClass {
    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * 1) Provide ONLY static fields, properties, and/or methods in this class.
     * 2) Do not create instances of this class!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @static
     * @public @method
     * Determines if the provided value is not a number or actually equals Number.NaN.
     * @param {any} [value=EzNumber.NAN]
     * @returns {boolean}
     */
    static isNaN(value = EzNumber.NAN) {
        return !EzNumber.isNumber(value) || Number.isNaN(value);
    }

    /**
     * @static
     * @public @method
     * Determines if the provided value is not a number or actually equals Number.NaN.
     * Alias for: (preferred method) EzNumber.isNAN(value)
     * @param {any} [value=EzNumber.NAN]
     * @returns {boolean}
     * @deprecated
     * Migrate to: EzNumber.isNaN(value)
     */
    static isNAN = EzNumber.isNaN;

    /**
     * @static
     * @public @method
     * Determines if the passed value is a number and of string or numbe type.
     * @param {any} value
     * @returns {boolean}
     */
    static isNumber(value) {
        return undefined !== value && null !== value && 'number' === typeof value && !Number.isNaN(value);
    }

    /**
     * @static
     * @public @method
     * Returns the provided value if it is a valid number, Otherwise, returns null
     * @param {*} value
     * @returns {number|null}
     */
    static numberOrNull(value) {
        return EzNumber.isNumber(value)
            ? value
            : null;
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a positibe number (aka greater than or equal to zero).
     * Ohterwise, returns null.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @returns {null|number}
     */
    static positiveNumberOrNull(aValue = null) {
        return EzNumber.positiveNumberOrDefault(aValue, null);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a positibe number (aka greater than or equal to zero).
     * Ohterwise, returns EzNumber.ZERO.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @returns {number}
     */
    static positiveNumberOrZero(aValue = EzNumber.ZERO) {
        return EzNumber.positiveNumberOrDefault(aValue, EzNumber.ZERO);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a positive number (aka greater than or equal to zero).
     * Ohterwise, returns EzNumber.MINUS_ONE.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @returns {number}
     */
    static positiveNumberOrNegativeOne(aValue = EzNumber.MINUS_ONE) {
        return EzNumber.positiveNumberOrDefault(aValue, EzNumber.MINUS_ONE);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a negative number (aka zero is greater than aValue).
     * Ohterwise, returns the provided defaultValue.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @param {undefined|null|number|NaN|any} defaultValue
     * Default value to return when the provided aValue is not a negative number.
     * The default value can be any value, does not need to be a number.
     * Will return null for undefined.
     * @returns {null|number|NaN|any}
     */
    static positiveNumberOrDefault(aValue = EzNumber.NaN, defaultValue = null) {
        if (EzNumber.isNaN(aValue) || !EzNumber.isNumber(aValue)) {
            return defaultValue;
        }

        return EzNumber.ZERO <= aValue
            ? aValue
            : EzObject.assignOrNull(defaultValue);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a negative number (aka zero is greater than the provided aValue).
     * Ohterwise, returns null.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @returns {null|number}
     */
    static negativeNumberOrNull(aValue = null) {
        return EzNumber.negativeNumberOrDefault(aValue, null);
    }


    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a negative number (aka zero is greater than the provided aValue).
     * Ohterwise, returns EzNumber.ZERO.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @returns {number}
     */
    static negativeNumberOrZero(aValue = EzNumber.ZERO) {
        return EzNumber.negativeNumberOrDefault(aValue, EzNumber.ZERO);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a negative number (aka zero is greater than the provided aValue).
     * Ohterwise, returns EzNumber.MINUS_ONE.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @returns {number}
     */
    static negativeNumberOrNegativeOne(aValue = EzNumber.MINUS_ONE) {
        return EzNumber.negativeNumberOrDefault(aValue, EzNumber.MINUS_ONE);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it is a negative number (aka zero is greater than the provided aValue).
     * Ohterwise, returns the provided defaultValue.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @param {undefined|null|number|NaN|any} defaultValue
     * Default value to return when the provided aValue is not a negative number.
     * The default value can be any value, does not need to be a number.
     * Will return null for undefined.
     * @returns {null|number|NaN|any}
     */
    static negativeNumberOrDefault(aValue = EzNumber.NaN, defaultValue = null) {
        if (EzNumber.isNaN(aValue) || !EzNumber.isNumber(aValue)) {
            return defaultValue;
        }

        return EzNumber.ZERO > aValue
            ? aValue
            : EzObject.assignOrNull(defaultValue);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it equals zero.
     * Ohterwise, returns null.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @returns {null|number}
     */
    static zeroOrNull(aValue = null) {
        return EzNumber.zeroOrDefault(aValue, null);
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue if it equals zero.
     * Ohterwise, returns the provided defaultValue.
     * @param {undefined|null|number|NaN|any} [aValue=EzNumber.NaN]
     * @param {undefined|null|number|NaN|any} defaultValue
     * Default value to return when the provided aValue is not equal to zero.
     * The default value can be any value, does not need to be a number.
     * Will return null for undefined.
     * @returns {null|number|NaN|any}
     */
    static zeroOrDefault(aValue = EzNumber.NaN, defaultValue = null) {
        if (EzNumber.isNaN(aValue) || !EzNumber.isNumber(aValue)) {
            return defaultValue;
        }

        return EzNumber.ZERO === aValue
            ? aValue
            : EzObject.assignOrNull(defaultValue);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value as a string. If the provided value is not a number, 'NaN' is returned.
     * @param {*|number} value
     * @returns {string}
     */
    static asString(value = Number.NaN) {
        return EzNumber.asStringOrDefault(
            value,
            Number.NaN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value in one of the following ways:
     * 1) If the provided value equals NaN, then the string 'NaN' is returned.
     * 2) If the provided value is a number, then the number represented as a string is returned.
     * 3) If the provided value is a string with a length, then the value is returned as is.
     * 4) If the provided value is not a number, then the defaultValue is returend.
     * @param {number} aNumber
     * Default: NaN
     * @param {undefined|null|number|string|boolean|*} defaultValue
     * Default: NaN
     * @returns {string}
     */
    static asStringOrDefault(aNumber = Number.NaN, defaultValue = Number.NaN) {
        if (Number.isNaN(aNumber)) {
            // If the value is NaN, then return 'NaN'
            return 'NaN';
        }

        if (EzNumber.isNumber(aNumber)) {
            return aNumber.toString();
        }

        if ('string' === typeof aNumber && 0 < aNumber.length) {
            // If the value is already a string, return the value as provided
            return aNumber;
        }

        // Otherwise, return the defaultValue.toString()
        return defaultValue;
    }

    /**
     * @static
     * @public @method
     * Returns the provided value if it is a valid number, Otherwise, returns null
     * @param {undefined|null|number|NaN|any} value
     * @returns {number|null}
     */
    static numberOrNaN(value) {
        return EzNumber.isNumber(value)
            ? value
            : EzNumber.NaN;
    }

    /**
     * @static
     * @public @method
     * Returns the provided value if it is a valid number, Otherwise, returns the provided defaultValue value.
     * @param {number|any} value
     * @param {any} defaultValue
     * @returns {number|any}
     */
    static numberOrDefault(value, defaultValue) {
        return EzNumber.isNumber(value)
            ? value
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value (assumed a string) to a floating point number using parseFloat.
     * If value cannot transform to a number, then EzNumber.NaN is returned.
     * @param {string|number|boolean} value
     * @returns {number|EzNumber.NaN}
     */
    static asFloat(value) {
        return EzNumber.asFloatOrNaN(value);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided aValue to a floating point number (usin asFloat).
     * If value cannot transform to a number, then null is returned.
     * @param {string|number|boolean} value
     * @returns {number|null}
     */
    static asFloatOrNull(value = null) {
        return EzNumber.asFloatOrDefault(value, null);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided aValue to a floating point number (usin asFloat).
     * If value cannot transform to a number, then EzNumber.NaN is returned.
     * @param {string|number|boolean} value
     * @returns {number|NaN}
     */
    static asFloatOrNaN(value) {
        return EzNumber.asFloatOrDefault(value, EzNumber.NaN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value to a number. If the value cannot transform to a number, then the defaultValue is returned.
     * @param {string|number|boolean} value
     * Default: 0.0
     * @param {any} defaultValue
     * Default value: NaN
     * @returns {number}
     */
    static asFloatOrDefault(value = null, defaultValue = Number.NaN) {
        if (EzNumber.isNumber(value)) {
            return EzNumber.ezParseFloat(value, defaultValue);
        }

        if ('boolean' === typeof value) {
            return EzBoolean.isTrue(value)
                ? EzNumber.ezParseFloat(EzNumber.ONE, defaultValue)
                : EzNumber.ezParseFloat(EzNumber.ZERO, defaultValue);
        }

        if ('string' !== typeof value) {
            return defaultValue;
        }

        return EzNumber.ezParseFloat(value);
    }

    /**
     * @static
     * @public @function
     * Transforms the provided value to a number. If the value cannot transform to a number, then NaN is returned.
     * Alias for: (preferred method) EzNumber.asFloatOrDefault(value, EzNumber.NaN)
     * @param {string|number|boolean} value
     * @param {any} defaultValue
     * @returns {number|any}
     */
    static ezParseFloat(value, defaultValue) {
        try {
            return Number.parseFloat(value);
        } catch (err) {
            const em = err?.message && 'string' === typeof err.message && 0 < err.message.length
                ? err.message
                : `Unexpected error while executing Number.parseFloat(${value}).`;

            globalThis?.window?.console?.error(
                `Failed to transform the provided value into a floating point number due to the following error: ${em}.` +
                `Therefore, will return the provided default value of ${defaultValue} instead.` +
                `[Value parameter: ${EzObject.isValid(value) ? value : 'undefined\null'}]`);
        }
    }

    /**
      * @static
      * @public @method
      * Converts a string value to a Javascript number (using parseInt).
      * If the stringValue is null, undefined, or not a number then the provided defaultValue is returned.
      * @param {undefined|null|string} aValue
      * Default: NaN (returns defaultValue)
      * @param {undefined|null|NaN|number|*} defaultValue
      * Default: NaN
      * @param {undefined|null|number} radix
      * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
      * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
      * Default: 10
      * @returns {number}
      * @deprecated
      * Migrate to one of the following:
      * 1) EzNumber.asIntOrDefault(aValue, defaultValue, radix)
      * 2) EzNumber.asFloatOrDefault(aValue, defaultValue)
      */
    static strToNumberDefault(aValue = null, defaultValue = EzNumber.NaN, radix = EzNumber._10) {
        return EzNumber.asNumberOrDefault(aValue, defaultValue, radix);
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt)
     * If the stringValue is null, undefined, or not a number, then NaN is returned.
     * @param {undefined|null|string} aValue
     * @param {undefined|null|number} radix
     * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Default: 10
     * @returns {number}
     * Returns the number as an integer
     * @deprecated
      * Migrate to one of the following:
      * 1) EzNumber.asIntOrDefault(aValue, defaultValue, radix)
      * 2) EzNumber.asFloatOrDefault(aValue, defaultValue)
     */
    static strToNumber = EzNumber.asInt;

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt).
     * If the stringValue is null, undefined, or not a number then the provided defaultValue is returned.
     * @param {number|boolean|string} value
     * Default: NaN (returns defaultValue)
     * @param {any} defaultValue
     * Default: NaN
     * @param {undefined|null|number} [radix=EzNumber.TEN]
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * @returns {number|any}
     * @deprecated
     * Migrate to one of the following:
     * 1) EzNumber.asIntOrDefault(aValue, defaultValue, radix)
     * 2) EzNumber.asFloatOrDefault(aValue, defaultValue)
     */
    static asNumberOrDefault(value, defaultValue, radix = EzNumber.TEN) {
        return EzNumber.asIntOrDefault(value, defaultValue, radix);
    }

    /**
     * @static
     * @public @method
     * Converts the provided value to a integer number using the provided raidx (using Number.parseInt(value, radix))
     * If the provided value is not a string number integer, then EzNumber.NaN is returned.
     * @param {string} value
     * @param {undefined|null|number} [radix=EzNumber.TEN]
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Any other value will restul in the default value of EzNumber.TEN
     * @returns {number|EzNumber.NaN}
     * @deprecated
     * Migrate to one of the following:
     *  1) EzNumber.asIntOrDefault(value, defautlValue, radix = EzNumber.TEN)
     *  2) EzNumber.asFloatOrDefault(value, defaultValue)
     *  3) EzNumber.asInt(value, radix = EzNumber.TEN)
     *  4) EzNumber.asIntOrNull(aValue = null, radix = EzNumber.TEN)
     *  5) EzNumber.asFloat(value)
     */
    static asNumber(value, radix = EzNumber.TEN) {
        return EzNumber.asIntOrDefault(value, EzNumber.NaN, radix)
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt)
     * If the stringValue is null, undefined, or not a number, then NaN is returned.
     * @param {undefined|null|string} value
     * @param {undefined|null|number} [radix=EzNumber.TEN]
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * ny other value will restul in the default value of EzNumber.TEN
     * @returns {number|NaN}
     * Returns the number as an integer
     */
    static asInt(value, radix = EzNumber.TEN) {
        return EzNumber.asIntOrDefault(value, EzNumber.NaN, radix);
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript integer (using parseInt).
     * If the stringValue is null, undefined, or not a number then null is returned
     * @param {undefined|null|string} aValue
     * Default: null
     * @param {undefined|null|number} radix
     * A value of undefined, null, NaN or zero will result in a default radix of 10 (decimal).
     * An integer between 2 and 36 that represents the radix (the base in mathematical numeral systems) of the string.
     * Default: 10
     * @returns {null|number}
     * Returns the number as an integer
     */
    static asIntOrNull(aValue = null, radix = EzNumber.TEN) {
        return EzNumber.asIntOrDefault(aValue, null, radix);
    }

    /**
     * @static
     * @public @method
     * Converts a string value to a Javascript number (using parseInt).
     * If the stringValue is null, undefined, or not a number then the provided defaultValue is returned.
     * @param {string} value
     * Default: EzNumber.NaN (returns defaultValue)
     * @param {any} [defaultValue=EzNumber.NaN]
     * Default: EzNumber.NaN
     * @param {undefined|null|number} [radix=EzNumber.TEN]
     * A valid radix positive integer between 2 and 36 (the base in mathematical numeral systems)
     * @returns {any|number}
     * Returns the number as an integer
     */
    static asIntOrDefault(value, defaultValue = EzNumber.NaN, radix = EzNumber.TEN) {
        if (!EzNumber.isNumber(radix)) {
            radix = EzNumber.TEN;
        } else if (!(radix > EzNumber.TWO && radix < EzNumber._36)) {
            throw new EzBadParamException(
                { radix },
                EzNumber,
                EzNumber.asIntOrDefault,
                'The radix must be a positive integer value from 2 to 36.');
        }

        if (EzNumber.isNumber(value)) {
            return EzNumber.ezParseInt(value, defaultValue, radix);
        }

        if ('boolean' === typeof value) {
            return EzBoolean.isTrue(value)
                ? EzNumber.ezParseInt(EzNumber.ONE, defaultValue, radix)
                : EzNumber.ezParseInt(EzNumber.ZERO, defaultValue, radix)
        }

        if (!EzObject.isValid(value) || 'string' !== typeof value || EzNumber.ZERO === value.length) {
            globalThis.console.warn(
                `The provided value parameter is not a valid integer number as a string than can transform to an number integer with radix ${radix}.` +
                `Therefore, the default value of ${defaultValue} will get returned.`,
                `[Value parameter: ${EzObject.isValid(value) ? value : 'undefined\null'}]`);

            // fall to NaN if the string value is null, empty, or undefined
            return defaultValue;
        }

        return EzNumber.ezParseInt(value, defaultValue, radix);
    }

    /**
     * @static
     * @public @function
     * Transforms the provided value (assumed to be an integer value as a string) to a integer number using the provided radix.
     * @param {string} value
     * @param {any} defaultValue
     * Value to return if the provided value cannot be transformed to an integer number with the provided radix.
     * The default value can be any value, does not need to be a number. The default value is NOT transformed into the provided radix even if it is a
     * valid number.
     * @param {undefined|null|number} [radix=EzNumber.TEN]
     * @returns {number|NaN|any}
     */
    static ezParseInt(value, defaultValue, radix = EzNumber.TEN) {
        radix = EzNumber.asValidRadix(radix, EzNumber.TEN);

        try {
            const parsedValue = Number.parseInt(
                value,
                radix);

            // Number.parseInt returns NaN for an unparseable value rather than throwing, so the
            // catch below never fires for that case. Without this check every *OrDefault/*OrNull
            // caller receives NaN instead of the default it asked for.
            return Number.isNaN(parsedValue)
                ? defaultValue
                : parsedValue;
        } catch (err) {
            const em = err?.message && 'string' === typeof err.message && EzNumber.ZERO < err.message.length
                ? err.message
                : `Unexpected error when calling Number.parseInt(${value}, ${radix}).`

            globalThis?.ezApi?.ezclocker?.ezLogger?.debug(
                `Failed to transform the value parameter to a integer number with radix ${radix} due to the following error: ${em}.` +
                `Will instead return the provided defaultValue of ${defaultValue}.
                [Value parameter: ${EzObject.isValid(value) ? value : 'undefined\null'}]`,
                err);

            return defaultValue;
        }
    }

    /**
     * @static
     * @public @function
     * Evalues the provided numberValue:
     * 1) If it is a valid radix (aka an integer between 2 and 36), the numberValue is returned.
     * Otherwise, if the provided defaultRadix is a valid radix (aka an integer between 2 and 36) then the defaultRadix is returned.
     * Otherwise, a raix of 10 is returned.
     * @param {number} numberValue
     * @param {undefined|null|number} [defaultRadix=EzNumber.TEN]
     * A valid radix positive integer between 2 and 36 (the base in mathematical numeral systems)
     * @returns {number}
     */
    static asValidRadix(numberValue, defaultRadix = EzNumber.TEN) {
        if (!EzNumber.isNumber(defaultRadix) || EzNumber.TWO > defaultRadix || EzNumber._36 < defaultRadix) {
            defaultRadix = EzNumber.TEN;
        }

        if (!EzNumber.isNumber(numberValue) || EzNumber.TWO > numberValue || EzNumber._36 < numberValue) {
            return defaultRadix;
        }

        return numberValue;
    }

    /**
     * @static
     * @public @method
     * Transforms the provided string value into an integer.
     * Returns NaN if the provided string value cannot transform to an integer.
     * @param {string} value
     * @returns {number}
     * @deprecated
     * Migrate to one of the following:
     *  1) EzNumber.asIntOrDefault(value, defautlValue, radix = EzNumber.TEN)
     *  2) EzNumber.asInt(value, radix = EzNumber.TEN)
     *  3) EzNumber.asIntOrNull(aValue = null, radix = EzNumber.TEN)
     *  4) EzNumber.asFloatOrDefault(value, defaultValue)
     *  5) EzNumber.asFloat(value)
     *  6) EzNumber.asFloatOrNull(value)
     *  7) EzNumber.asFloatOrNaN(value)
     */
    static strToInt(value) {
        return EzNumber.asIntOrDefault(value, EzNumber.NaN, EzNumber.TEN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided value into an integer number with radix 10.
     * If the provided value cannot transform to an integer number with radix 10, then the provided defaultValue is returned.
     * @param {string} value
     * @param {any} defaultValue
     * Can be any value (does not need to be an number). If the defaultValue is a number it is NOT transformed to radix 10 before returning.
     * @returns {undefined|null|number|*}
     * @deprecated
     * Migrate to: EzNumber.asIntOrDefault(value, defautlValue, radix = EzNumber.TEN)
     */
    static strToIntDefault(value, defaultValue) {
        return EzNumber.asIntOrDefault(value, defaultValue, EzNumber.TEN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided string value into a floating point number by calling EzNumber.strToFloatDefault(stringValue, NaN)
     * @param {string} value
     * Expecting: A floating point number represented as a string.
     * @returns {number}
     */
    static strToFloat(stringValue) {
        if (!EzObject.isValid(stringValue) || 'string' !== typeof stringValue || EzNumber.ZERO === stringValue.length) {
            globalThis.console.warn(
                `Returning NaN from call to EzNumber.strToFloat(...): ` +
                `The provided stringValue of ${stringValue} is either undefined, null, not a string, or a string with zero length.`);

            return Number.NaN;
        }

        return EzNumber.strToFloatDefault(stringValue, Number.NaN);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided stringValue into a floating point number.
     * If the stringValue cannot transform to a floating point number then the defaultValue is returned instead.
     * @param {string} value
     * Expecting: A floating point number represented as a string.
     * @param {undefined|null|number|*} defaultValue
     * Can be any value (does not have to be a floating point value)
     * @returns {undefined|null|number|*}
     */
    static strToFloatDefault(stringValue, defaultValue = Number.NaN) {
        if (!EzObject.isValid(stringValue) || 'string' !== typeof stringValue || EzNumber.ZERO === stringValue.length) {
            globalThis.console.warn(
                `Returning the provided default value of ${defaultValue} from call to EzNumber.strToFloatDefault(...): ` +
                `The provided stringValue of ${stringValue} is either undefined, null, an empty string, or not a string.`);

            return defaultValue;
        }

        try {
            const parseFloatResult = Number.parseFloat(stringValue);

            if (Number.isNaN(parseFloatResult)) {
                globalThis.console.warn(
                    `Returning the provided default value of ${defaultValue} from call to EzNumber.strToFloatDefault(...): ` +
                    `The provided stringValue of ${stringValue} does not transform to a valid floating point number.`);

                return defaultValue;
            }

            return parseFloatResult;
        } catch (err) {
            globalThis.console.warn(
                `Returning the provided default value of ${defaultValue} from call to EzNumber.strToFloatDefault(...) ` +
                `after failing to transform the provided stringValue of ${stringValue} ` +
                `to a floating point number due to the following error: ${err.message}.`);

            return defaultValue;
        }
    }

    /**
     * @static
     * @public @method
     * Determines: value >= aMinNumber && value <= aMaxNumber
     * @param {number} aNumber
     * @param {undefined|null|number} aMinNumber
     * If a number is not provided then aMinNumber is set equal Number.MIN_VALUE.
     * Default: Number.MIN_VALUE
     * @param {undefined|null|number} aMaxNumber
     * If a number is not provided then aMaxNumber is set equal to Number.MAX_VALUE.
     * Default: Number.MAX_VALUE
     * @returns {boolean}
     * Returns true if the provided aNumber is >= aMinNumber OR aNumber <= aMaxNumber.
     * Returns false otherwise.
     */
    static isNumberWithinRange(aNumber, aMinNumber = Number.MIN_VALUE, aMaxNumber = Number.MAX_VALUE) {
        if (!EzNumber.isNumber(aNumber)) {
            throw new EzBadParamException(
                'aNumber',
                EzNumber,
                EzNumber.isNumberWithinRange,
                `The provided value of ${aNumber} is not a valid number.`);
        }

        if (!EzNumber.isNumber(aMinNumber)) {
            aMinNumber = Number.MIN_VALUE;
        }

        if (!EzNumber.isNumber(aMaxNumber)) {
            aMaxNumber = Number.MAX_VALUE;
        }

        if (aMinNumber > aMaxNumber) {
            throw new EzBadParamException(
                'aMinNumber',
                EzNumber,
                EzNumber.isNumberWithinRange,
                'The aMinNumber parameter must be less than or equal to the aMaxNumber parameter.');
        }

        return aNumber >= aMinNumber && aNumber <= aMaxNumber;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided value param is between the aMinNumber and aMaxNumber range.
     * @param {number} aNumber
     * @param {undefined|null|number} aMinNumber
     * If aMinNumber is undefined or null then aMinNumber is set equal to Number.MIN_VALUE
     * Default: Number.MIN_VALUE
     * @param {number} aMaxNumber
     * If a aMaxNumber is undefined or null then aMaxNumber is set equal to Number.MAX_VALUE
     * Default: Number.MAX_VALUE
     * @returns {boolean}
     * 1) If the provided aNumber is not a valid number then an exception is thrown.
     * 2) If aMinNumber is greater than a aMaxNumber then an exception is thrown.
     * 3) Returns true if aNumber is > aMinNumber AND aNumber is < aMaxNumber
     */
    static isNumberBetweenRange(aNumber, aMinNumber = Number.MIN_VALUE, aMaxNumber = Number.MAX_VALUE) {
        if (!EzNumber.isNumber(aNumber)) {
            throw new EzBadParamException(
                'value',
                EzNumber,
                EzNumber.isNumberBetweenRange,
                `The provided value of ${aNumber} is not a valid number.`);
        }

        if (!EzNumber.isNumber(aMinNumber)) {
            aMinNumber = Number.MIN_VALUE;
        }

        if (!EzNumber.isNumber(aMaxNumber)) {
            aMaxNumber = Number.MAX_VALUE;
        }

        if (aMinNumber > aMaxNumber) {
            throw new EzBadParamException(
                'aMinNumber',
                EzNumber,
                EzNumber.isNumberBetweenRange,
                'The aMinNumber parameter must be less than or equal to the aMaxNumber parameter.');
        }

        return aNumber > aMinNumber && aNumber < aMaxNumber;
    }

    /**
     * @static
     * @public @method
     * If the provided aNumber is a valid number then the aNumber value is returned.
     * Otherwise, zero is returned.
     * @param {undefined|null|number|*} number
     * @returns {number}
     */
    static numberOrZero(aNumber) {
        return EzNumber.isNumber(aNumber)
            ? aNumber
            : EzNumber.ZERO;
    }

    /**
     * @static
     * @public @method
     * Returns the provided number as a currency string using the provided
     * EzLocale and EzCurrency values.
     * @param {number} aNumber
     * @param {string} ezLocal
     * A valid enumeration property value from /ezlibrary/locales/EzLocale.js
     * Default: 'en-us'
     * @param {string} ezCurrency
     * A valid enumeration property value from /ezlibrary/locales/EzCurrency.js
     * Default: 'USD'
     */
    static numberAsCurrencyString(aNumber, ezLocale, ezCurrency) {
        if (!EzObject.isValid(ezLocale) || 'string' !== typeof ezLocale) {
            ezLocale = 'en';
        }
        if (!EzObject.isValid(ezCurrency) || 'string' !== typeof ezCurrency) {
            ezCurrency = 'USD';
        }

        // Create our number formatter.
        const numberFormatter = new Intl.NumberFormat(
            ezLocale,
            {
                style: 'currency',
                currency: ezCurrency
            });

        return numberFormatter.format(aNumber);
    }

    /**
     * @static
     * @public @method
     * Formats the provided value to have the provided number of decimal places.
     * References:
     *     https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toFixed
     * @param {number} aNumber
     * @param {undefined|null|number} numberOfDecimalPlaces
     * Default: 0
     * @returns {number}
     * Returns a floating point number
     */
    static formatDecimal(aNumber, numberOfDecimalPlaces) {
        if (!EzNumber.isNumber(aNumber)) {
            throw new EzBadParamException(
                'aNumber',
                EzNumber,
                EzNumber.formatDecimal);
        }
        if (!EzNumber.isNumber(numberOfDecimalPlaces)) {
            numberOfDecimalPlaces = 0;
        }
        if (0 > numberOfDecimalPlaces || 100 < numberOfDecimalPlaces) {
            throw new EzBadParamException(
                'numberOfDecimalPlaces (must be between 0 and 100)',
                EzNumber,
                EzNumber.formatDecimal);
        }

        return Number.parseFloat(aNumber.toFixed(numberOfDecimalPlaces));
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number into a string padding a zero at the front if
     * the aNumber < 10;
     * @param {undefined|null|number} aNumber
     * Default: null
     * @returns {string}
     */
    static toIso2DigitValue(aNumber = null) {
        if (!EzNumber.isNumber(aNumber)) {
            return '00';
        }

        // Negative values cannot be represented as a 2 digit ISO field; `0${-1}` yields '0-1'.
        if (aNumber < EzNumber.ZERO) {
            return '00';
        }

        return aNumber < 10
            ? `0${aNumber}`
            : EzNumber.asString(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso date string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoDateString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso month string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoMonthString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso hours string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoHourString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso minutes string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoMinuteString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a two digit iso seconds string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoSecondString(aNumber = null) {
        return EzNumber.toIso2DigitValue(aNumber);
    }

    /**
     * @static
     * @public @method
     * Transforms the provided number to a three digit iso milliseconds string (appending 0 if necesesary)
     * @param {number} aNumber
     * @returns {string}
     */
    static toIsoMillisecondString(aNumber) {
        if (!EzNumber.isNumber(aNumber) || aNumber < EzNumber.ZERO) {
            return '000';
        }

        return aNumber.toString().padStart(3, '0');
    }

    /*
      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      * ONLY STATIC INITIALIZATION CODE AND PRIVATE STATIC CONSTANT BUILDERS BELOW THIS SECTION PLEASE
      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
      */

    /**
     * @static
     *
     * IMPORTANT: Keep the documentation updated if you add additional constants (below and in the class JSDoc above)
     *
     * @public @method
     * Returns the provided indexValue as a number to be used in an array, list, or other indexable structure.
     * If the indexValue is a string, the EzNumber.strToInt(idnexValue) is performed.
     * @param {String|Number} indexValue
     * DEFAULT: EzNumber.FIRST_INDEX
     * @returns {Number}
     */
    static atIndex(indexValue = EzNumber.FIRST_INDEX) {
        if (!EzNumber.isNumber(indexValue)) {
            if ('string' === typeof indexValue) {
                const indexAsNumber = EzNumber.strToInt(indexValue);

                if (EzNumber.isNaN(indexAsNumber)) {
                    throw new EzBadParamException(
                        'indexValue',
                        EzNumber,
                        EzNumber.atIndex);
                }

                return indexAsNumber;
            }

            throw new EzBadParamException(
                'indexValue',
                EzNumber,
                EzNumber.atIndex);
        }

        return indexValue;
    }

    /**
     * @static
     * @private @function
     * Adds the constants for number values
     *
     * IMPORTANT: Keep the documentation updated if you add additional constants (below and in the class JSDoc above)
     *
     * Adds the following static readonly properties to EzNumber:
     * 1) EzNumber._{aNumber} = {aNumber} for aNumber in the sequence of 0 to 25
     * 2) EzNumber.{numberWords[aNumber - 1]} = EzNumber._{aNumber} for aNumber in the sequence of 0 to 25
     * 3) EzNumber._{otherNumber} = {otherNumber} for otherNumber in the set of [30, 45, 100, 200, 300, 365, 400, 500, 600, 999, 9999]
     * 4) EzNumber.{otherNumberWords[otherNumberConstants.indexOf(otherNumber)]} = EzNumber._{otherNumber} for otherNumber in the set of
     *    30, 45, 100, 200, 300, 365, 400, 500, 600, 999, 9999
     * 5) EzNumber._n{Math.abs(negativeNumber)} = {negativeNumber} for negativeNumber in the set of
     *    [-1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13]
     * 6) EzNumber.MINUS_n{Math.abs(negativeNumber)} = EzNumber._n{Math.abs(negativeNumber)} for negativeNumber in the set of
     *    [-1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13]
     * 7) EzNumber.{negativeNumberWords[negativeNumberConstants.indexOf(negativeNumber)]} = EzNumber._n{Math.abs(negativeNumber)} for negativeNumber
     *    in the set of [-1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13]
     */
    static #buildEzNumberConstants() {
        const startSequenceOfNumberConstants = 0;

        const endSequenceOfNumberConstants = 30;

        const numberWords = [
            'ZERO',
            'ONE',
            'TWO',
            'THREE',
            'FOUR',
            'FIVE',
            'SIX',
            'SEVEN',
            'EIGHT',
            'NINE',
            'TEN',
            'ELEVEN',
            'TWELVE',
            'THIRTEEN',
            'FOURTEEN',
            'FIFTEEN',
            'SIXTEEN',
            'SEVENTEEN',
            'EIGHTEEN',
            'NINETEEN',
            'TWENTY',
            'TWENTY_ONE',
            'TWENTY_TWO',
            'TWENTY_THREE',
            'TWENTY_FOUR',
            'TWENTY_FIVE',
            'TWENTY_SIX',
            'TWENTY_SEVEN',
            'TWENTY_EIGHT',
            'TWENTY_NINE',
            'THIRTY'
        ];

        for (let aNumber = startSequenceOfNumberConstants;aNumber <= endSequenceOfNumberConstants;aNumber++) {
            const staticPropName = `_${aNumber}`;

            // Template: get _{aNumber}() { return {aNumber}; }
            EzNumber.addStaticPropertyToClass(
                this,
                staticPropName,
                aNumber);

            // Template: get {numberWords[aNumber]}() { return EzNumber._{aNumber}; }
            EzNumber.addStaticPropertyToClass(
                this,
                numberWords[aNumber],
                EzNumber[staticPropName]);
        }

        const otherYears = [
            1700,
            // Declaration of Independence
            1776,
            // Constitution of the United States of America
            1787,
            1800,
            // Emancipation Proclamation
            1863,
            1900,
            // World War I
            1915,
            1910,
            1920,
            // Stock Market Crash
            1929,
            1930,
            1940,
            // Attack on Pearl Harbor
            1941,
            // The Atomic Bombing of Hiroshima and Nagasaki
            1945,
            1950,
            1960,
            // Assassination of Martin Luther King, Jr.
            1968,
            // Brandy's Birth Year
            1969,
            1970,
            // Jason's Birth Year
            1971,
            1980,
            // Williamr's Birth Year
            1988,
            // Highschool Granduation Year
            1990,
            2000,
            // September 11 Attacks
            2001,
            2010,
            // EzClocker's Birth Year
            2014
        ];

        for (let otherYear of otherYears) {
            EzNumber.addStaticPropertyToClass(
                this,
                `_${otherYear}`,
                otherYear);
        }

        const additionalYearStart = 2015

        for (let additionalYear = additionalYearStart;additionalYear <= new Date().getFullYear();additionalYear++) {
            EzNumber.addStaticPropertyToClass(
                this,
                `_${additionalYear}`,
                additionalYear);
        }

        const otherNumberConstantsDef = [
            {
                n: 36,
                sn: 'THIRTY_SIX'
            },
            {
                n: 40,
                sn: 'FORTY'
            },
            {
                n: 45,
                sn: 'FORTY_FIVE'
            },
            {
                n: 50,
                sn: 'FIFTY'
            },
            {
                n: 51,
                sn: 'FIFTY_ONE'
            },
            {
                n: 52,
                sn: 'FIFTY_TWO'
            },
            {
                n: 53,
                sn: 'FIFTY_THREE'
            },
            {
                n: 54,
                sn: 'FIFTY_FOUR'
            },
            {
                n: 55,
                sn: 'FIFTY_FIVE'
            },
            {
                n: 59,
                sn: 'FIFTY_NINE'
            },
            {
                n: 60,
                sn: 'SIXTY'
            },
            {
                n: 70,
                sn: 'SEVENTY'
            },
            {
                n: 80,
                sn: 'EIGHTY'
            },
            {
                n: 90,
                sn: 'NINETY'
            },
            {
                n: 100,
                sn: 'ONE_HUNDRED'
            },
            {
                n: 200,
                sn: 'TWO_HUNDRED'
            },
            {
                n: 300,
                sn: 'THREE_HUNDRED'
            },
            {
                n: 365,
                sn: 'THREE_HUNDRED_SIXTY_FIVE'
            },
            {
                n: 400,
                sn: 'FOUR_HUNDRED'
            },
            {
                n: 500,
                sn: 'FIVE_HUNDRED'
            },
            {
                n: 600,
                sn: 'SIX_HUNDRED'
            },
            {
                n: 999,
                sn: 'NINE_HUNDRED_NINETY_NINE'
            },
            {
                n: 9999,
                sn: 'NINE_THOUSAND_NINE_HUNDRED_NINETY_NINE'
            }
        ];

        for (const element of otherNumberConstantsDef) {
            const otherNumber = element.n;

            const staticPropName = `_${otherNumber}`;

            // Template: get _{otherNumber}() { return {otherNumber}; }
            EzNumber.addStaticPropertyToClass(
                this,
                staticPropName,
                otherNumber);

            // Template: get {otherNumberWords[i]}() { return EzNumber._{otherNumber}; }
            EzNumber.addStaticPropertyToClass(
                this,
                element.sn,
                EzNumber[staticPropName]);
        }

        const otherLargeNumbers = [
            180,
            401,
            450,
            480,
            512,
            525,
            640,
            650,
            675,
            700,
            720,
            750,
            800,
            900,
            1024,
            1080,
            1280,
            1440,
            1600,
            2048,
            3840,
            4096,
            6400,
            6880,
            7680,
            8192,
            24576,
            32768
        ];

        for (let otherLargeNumber of otherLargeNumbers) {
            // EzNumber._{otherLargeNumber} = {otherLargeNumber}
            EzNumber.addStaticPropertyToClass(
                this,
                `_${otherLargeNumber}`,
                otherLargeNumber);
        }

        const monthsDaysYearsHoursSecondsMilliseconds = [
            // Milliseconds in second: 1000
            1000,
            // Milliseconds in minute: 60000
            // 60000
            (60 * 1000),
            // Milliseconds in a hour: 3600000
            // 3600000
            (60 * 60 * 1000),
            // Milliseconds in a day: 86400000
            // 86400000
            (24 * 60 * 60 * 1000),
            // Milliseconds in a week: 604800000
            // 604800000
            (7 * 24 * 60 * 60 * 1000),
            // Milliseconds in a month: 2628000000 (assuming 30.44 days in a month)
            2628000000,
            // Average milliseconds in a month: 2592000000 (assuming 30 days in a month)
            // 2592000000
            (30 * 24 * 60 * 60 * 1000),
            // Milliseconds in a year: 31536000000 (assuming 365.25 days in a year)
            31536000000,
            // Average milliseconds in a year: 31536000000 (assuming 365 days in a year),
            /* Value already exists
            // 31536000000,
            (365 * 24 * 60 * 60 * 1000),
            */

            // Seconds in hour: 3600
            3600,
            // Seconds in a day: 86400
            // 86400
            (24 * 3600),
            // Seconds in a week: 604800
            // 604800
            (7 * 24 * 3600),
            // Seconds in a month: 2628000 (assuming 30.44 days in a month)
            2628000,
            // Average seconds in a month: 2592000 (assuming 30 days in a month)
            // 2592000
            (30 * 24 * 3600),
            // Seconds in a year: 31536000 (assuming 365.25 days in a year)
            31536000,
            // Average seconds in a year: 31536000 (assuming 365 days in a year)
            /* Value already exists
            // 31536000,
            (365 * 24 * 3600),
            */

            // Minutes in a day
            /* Value already exits
            1440,
            */
            // Minutes in a week: 10080
            // 10080
            (7 * 1440),
            // Minutes in a month: 43800 (assuming 30.44 days in a month)
            43800,
            // Average minutes in a month: 43200 (assuming 30 days in a month)
            // 43200
            (30 * 1440),
            // Minutes in a year: 525600 (assuming 365.25 days in a year)
            525600,
            // Average minutes in a year: 525600 (assuming 365 days in a year)
            /* Value already exists
            // 525600
            (365 * 1440),
            */

            // Hours in a day
            /* Value already exits
            24,
            */
            // Hours in a week: 168
            // 168
            (7 * 24),
            // Hours in a month: 730.56 (assuming 30.44 days in a month)
            730.56,
            // Hours in a month: 720 (assuming 30 days in a month)
            // 720
            /*  Value already exists
            (30 * 24),
            */
            // Hours in a year: 8760 (assuming 365 days in a year)
            // 8760
            (365 * 24),

            // Days in a week
            /* value already exists
            7,
            */
            // Days in a month (assuming 30.44 days in a month)
            30.44,
            // Average days in a month (assuming 30 days in a month)
            /* Value already exists
            30,
            */
            // Days in a year (assuming 365.25 days in a year)
            365.25,
            // Average days in a year (assuming 365 days in a year)
            /* value already exists
            // 365,
            */

            // Weeks in a month (assuming 4.345 weeks in a month)
            4.345,
            // Average weeks in a month
            /* Value already exists
            4,
            */
            // Weeks in a year (assuming 52.14 weeks in a year)
            52.14
            // Average weeks in a year (assuming 52 weeks in a year)
            /* Value already exists
            ,52
            */
        ];

        for (let largeNumber of monthsDaysYearsHoursSecondsMilliseconds) {
            EzNumber.addStaticPropertyToClass(
                this,
                `_${largeNumber}`,
                largeNumber);
        }

        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_SECOND',
            EzNumber._1000);

        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_MINUTE',
            EzNumber._60000);

        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_HOUR',
            EzNumber._3600000);

        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_DAY',
            EzNumber._86400000);

        // Milliseconds in a week is based on the standard number of milliseconds in a week (7 days in a week and 24 hours in a day)
        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_WEEK',
            EzNumber._604800000);

        // Milliseconds in a month is based on the average number of milliseconds in a month (assuming 30.44 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_MONTH',
            EzNumber._2628000000);

        // Milliseconds in a year is based on the average number of milliseconds in a year (assuming 365.25 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_YEAR',
            EzNumber._31536000000);

        // Average milliseconds in a year is based on the average number of milliseconds in a year (assuming 365 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'MILLISECONDS_IN_YEAR_365',
            EzNumber._31536000000);

        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_MINUTE',
            EzNumber._60);

        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_HOUR',
            EzNumber._3600);

        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_DAY',
            EzNumber._86400);

        // Seconds in a week is based on the standard number of seconds in a week (7 days in a week and 24 hours in a day)
        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_WEEK',
            EzNumber._604800);

        // Seconds in a month is based on the average number of seconds in a month (assuming 30.44 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_MONTH',
            EzNumber._2628000);
        // Average seconds in a month is based on the average number of seconds in a month (assuming 30 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_MONTH_30',
            EzNumber._2592000);

        // Seconds in a year is based on the average number of seconds in a year (assuming 365.25 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_YEAR',
            EzNumber._31536000);

        // Average seconds in a year is based on the average number of seconds in a year (assuming 365 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'SECONDS_IN_YEAR_365',
            EzNumber._31536000);

        EzNumber.addStaticPropertyToClass(
            this,
            'MINUTS_IN_HOUR',
            EzNumber._60);

        EzNumber.addStaticPropertyToClass(
            this,
            'MINUTES_IN_DAY',
            EzNumber._1440);

        // Minutes in a week is based on the standard number of minutes in a week (7 days in a week and 24 hours in a day)
        EzNumber.addStaticPropertyToClass(
            this,
            'MINUTES_IN_WEEK',
            EzNumber._10080);

        // Minutes in a month is based on the average number of minutes in a month (assuming 30.44 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'MINUTES_IN_MONTH',
            EzNumber._43800);

        // Average minutes in a month (assuming 30 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'MINUTES_IN_MONTH_30',
            EzNumber._43200);

        // Minutes in a year is based on the average number of minutes in a year (assuming 365.25 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'MINUTES_IN_YEAR',
            EzNumber._525600);

        // Average minutes in a year is based on the average number of minutes in a year (assuming 365 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'MINUTES_IN_YEAR_365',
            EzNumber._525600);

        EzNumber.addStaticPropertyToClass(
            this,
            'HOURS_IN_DAY',
            EzNumber._24);

        // Hours in a week is based on the standard number of hours in a week (7 days in a week and 24 hours in a day)
        EzNumber.addStaticPropertyToClass(
            this,
            'HOURS_IN_WEEK',
            EzNumber._168);

        // Hours in a month is based on the average number of hours in a month (assuming 30.44 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'HOURS_IN_MONTH',
            EzNumber._730_56);

        // Average hours in a month (assuming 30 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'HOURS_IN_MONTH_30',
            EzNumber._720);

        // Hours in a year is based on the average number of hours in a year (assuming 365.25 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'HOURS_IN_YEAR',
            EzNumber._8760);

        // Average hours in a year (assuming 365 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'HOURS_IN_YEAR_365',
            EzNumber._8760);

        // Days in a week is based on the standard number of days in a week (7 days in a week)
        EzNumber.addStaticPropertyToClass(
            this,
            'DAYS_IN_WEEK',
            EzNumber._7);

        // Days in a month is based on the average number of days in a month (assuming 30.44 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'DAYS_IN_MONTH',
            EzNumber._30);

        // Average days in a month (assuming 30 days in a month)
        EzNumber.addStaticPropertyToClass(
            this,
            'DAYS_IN_MONTH_30',
            EzNumber._30);

        // Days in a year is based on the average number of days in a year (assuming 365.25 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'DAYS_IN_YEAR',
            EzNumber._365);

        // Average days in a year (assuming 365 days in a year)
        EzNumber.addStaticPropertyToClass(
            this,
            'DAYS_IN_YEAR_365',
            EzNumber._365);

        const negatvieNumberConstantDefs = [
            {
                n: -13,
                sn: 'MINUS_THIRTEEN'
            },
            {
                n: -12,
                sn: 'MINUS_TWELVE'
            },
            {
                n: -11,
                sn: 'MINUS_ELEVEN'
            },
            {
                n: -10,
                sn: 'MINUS_TEN'
            },
            {
                n: -9,
                sn: 'MINUS_NINE'
            },
            {
                n: -8,
                sn: 'MINUS_EIGHT'
            },
            {
                n: -7,
                sn: 'MINUS_SEVEN'
            },
            {
                n: -6,
                sn: 'MINUS_SIX'
            },
            {
                n: -5,
                sn: 'MINUS_FIVE'
            },
            {
                n: -4,
                sn: 'MINUS_FOUR'
            },
            {
                n: -3,
                sn: 'MINUS_THREE'
            },
            {
                n: -2,
                sn: 'MINUS_TWO'
            },
            {
                n: -1,
                sn: 'MINUS_ONE'
            }
        ];

        for (const negatvieNumberConstantDef of negatvieNumberConstantDefs) {
            // Values -1..-12
            let negativeNumber = negatvieNumberConstantDef.n;

            // '_n{-13 through -1}'
            let staticPropName1 = `_n${Math.abs(negativeNumber)}`;

            // 'MINUS_n{-13 through -1}'
            let staticPropName2 = `MINUS_n${Math.abs(negativeNumber)}`;

            // 'MINUS_THIRTEEN', 'MINUS_TWELVE', ..., 'MINUS_ONE'
            let staticPropName3 = negatvieNumberConstantDef.sn;

            EzNumber.addStaticPropertyToClass(
                this,
                staticPropName1,
                negativeNumber);

            EzNumber.addStaticPropertyToClass(
                this,
                staticPropName2,
                EzNumber[staticPropName1]);

            EzNumber.addStaticPropertyToClass(
                this,
                staticPropName3,
                EzNumber[staticPropName1]);
        }

        // Additional special number constants
        EzNumber.addStaticPropertyToClass(
            this,
            'LENGTH_ZERO',
            EzNumber['_0']);

        EzNumber.addStaticPropertyToClass(
            this,
            'SIZE_ZERO',
            EzNumber['_0']);

        EzNumber.addStaticPropertyToClass(
            this,
            'FIRST_INDEX',
            EzNumber['_0']);

        EzNumber.addStaticPropertyToClass(
            this,
            'FALSE',
            EzNumber['_0']);

        EzNumber.addStaticPropertyToClass(
            this,
            'TRUE',
            EzNumber['_1']);

        EzNumber.addStaticPropertyToClass(
            this,
            'SECOND_INDEX',
            EzNumber['_1']);

        EzNumber.addStaticPropertyToClass(
            this,
            'THIRD_INDEX',
            EzNumber['_2']);

        EzNumber.addStaticPropertyToClass(
            this,
            'FOURTH_INDEX',
            EzNumber['_3']);
    }

    static #ezFloatConstants = [
        0.25,
        0.33,
        0.5,
        0.45,
        0.75,
        0.66
    ]

    /**
     * @static
     * @private @function
     * Builds float number constants using template: `_{floatNumber.toString().replace('.', '_')}f` = {floatNumber}
     * Example: EzNumber._0_25f = 0.25
     *
     */
    static #buildEzNumberFloatConstants() {
        EzNumber.addStaticPropertyToClass(
            this,
            'QUARTER',
            0.25);

        EzNumber.addStaticPropertyToClass(
            this,
            'ONE_QUARTER',
            EzNumber['QUARTER']);

        EzNumber.addStaticPropertyToClass(
            this,
            'ONE_HALF',
            0.50);

        EzNumber.addStaticPropertyToClass(
            this,
            'HALF',
            EzNumber['ONE_HALF']);

        EzNumber.addStaticPropertyToClass(
            this,
            'TWO_QUARTERS',
            EzNumber['HALF']);

        EzNumber.addStaticPropertyToClass(
            this,
            'THREE_QUARTERS',
            0.75);

        EzNumber.addStaticPropertyToClass(
            this,
            'THIRD',
            0.33);

       EzNumber.addStaticPropertyToClass(
            this,
            'ONE_THIRD',
            EzNumber['THIRD']);

        EzNumber.addStaticPropertyToClass(
            this,
            'TWO_THIRDS',
            0.66);

        for (let floatNumber of this.#ezFloatConstants) {
            EzNumber.addStaticPropertyToClass(
                this,
                `_${floatNumber.toString().replace('.', '_')}f`,
                floatNumber);
        }
    }

    /**
     * @private
     * @static @function
     * Adds the constants for number values as a string.
     *
     * IMPORTANT: Keep the documentation updated if you add additional constants (below and in the class JSDoc above)
     *
     * Adds the following static read-only properties to EzNumber:
     * 1) EzNumber.s{aNumber} = '{aNumber}' for aNumber in the sequence of 0 to 25
     * 2) EzNumber.s{otherNumber} = '{otherNumber}' for otherNumber in the set of 30, 45, 100, 200, 300, 365, 400, 500, 600, 999, 9999
     * 3) EzNumber.sNEG_{negativeNumber} = '{negativeNumber}' for negativeNumber in the set of -1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13
     * 4) EzNumber.sMINUS_{negativeNumber} = '{negativeNumber}' for negativeNumber in the set of -1, -2, -3, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13
     * 5) EzNumber.sMINUS_ONE = '-1', EzNumber.sMINUS_TWO = '-2', ..., EzNumber.sMINUS_THIRTEEN = '-13'
     */
    static #buildEzNumberStringConstants() {
        const startSequenceOfNumber = 0;

        const endSequenceNumber = 30;

        for (let i = startSequenceOfNumber;i <= endSequenceNumber;i++) {
            EzNumber.addStaticPropertyToClass(
                this,
                `s${i}`,
                `${i}`);
        }

        const otherNumbers = [
            40,
            45,
            50,
            51,
            52,
            53,
            54,
            55,
            56,
            57,
            58,
            59,
            60,
            70,
            80,
            90,
            100,
            200,
            300,
            365,
            400,
            500,
            600,
            999,
            5000,
            9999,
            30000
        ];

        for (const otherNumber of otherNumbers) {

            EzNumber.addStaticPropertyToClass(
                this,
                `s${otherNumber}`,
                `${otherNumber}`);
        }

        const negativeStringNumberConstantsDef = [
            {
                n: -1,
                sn: 'sMINUS_ONE'
            },
            {
                n: -2,
                sn: 'sMINUS_TWO'
            },
            {
                n: -3,
                sn: 'sMINUS_THREE'
            },
            {
                n: -4,
                sn: 'sMINUS_FOUR'
            },
            {
                n: -5,
                sn: 'sMINUS_FIVE'
            },
            {
                n: -6,
                sn: 'sMINUS_SIX'
            },
            {
                n: -7,
                sn: 'sMINUS_SEVEN'
            },
            {
                n: -8,
                sn: 'sMINUS_EIGHT'
            },
            {
                n: -9,
                sn: 'sMINUS_NINE'
            },
            {
                n: -10,
                sn: 'sMINUS_TEN'
            },
            {
                n: -11,
                sn: 'sMINUS_ELEVEN'
            },
            {
                n: -12,
                sn: 'sMINUS_TWELVE'
            },
            {
                n: -13,
                sn: 'sMINUS_THIRTEEN'
            }
        ];

        for (const element of negativeStringNumberConstantsDef) {
            const negativeNumber = element.n

            // sNEG_{1..13}
            const staticPropName1 = `sNEG_${Math.abs(negativeNumber)}`;

            EzNumber.addStaticPropertyToClass(
                this,
                staticPropName1,
                `${negativeNumber}`);

            EzNumber.addStaticPropertyToClass(
                this,
                element.sn,
                EzNumber[staticPropName1]);
        }
    }


    /**
     * @static
     * @private @function
     * Builds currency related constants such as CURRENCY_ZERO and sCURRENCY_ZERO for the EzNumber class.
     *
     * IMPORTANT: Keep the documentation updated if you add additional constants (below and in the class JSDoc above)
     *
     * Adds the following static read-only properties to EzNumber:
     * 1) EzNumber.CURRENCY_ZERO = 0;
     * 2) EzNumber.sCURRENCY_ZERO = '0.00';
     * 3) EzNumber.ZERO$ = EzNumber.CURRENCY_ZERO;
     */
    static #buildCurrencyConstants() {
        EzNumber.addStaticPropertyToClass(
            this,
            'CURRENCY_ZERO',
            0);

        EzNumber.addStaticPropertyToClass(
            this,
            'sCURRENCY_ZERO',
            '0.00');

        EzNumber.addStaticPropertyToClass(
            this,
            'ZERO$',
            EzNumber['CURRENCY_ZERO']);
    }

    /**
     * @static
     * @private @function
     * Builds NaN related constants such as NaN and NaN_AS_STRING for the EzNumber class.
     *
     * IMPORTANT: Keep the documentation updated if you add additional constants (below and in the class JSDoc above)
     *
     * Adds the following static read-only properties to EzNumber
     * 1) EzNumber.NaN = Number.NaN;
     * 2) EzNumber.NAN = EzNUmber.NaN;
     * 3) EzNumber.NaN_AS_STRING = 'NaN';
     * 4) EzNumber.NAN_AS_STRING = EzNumber.NaN_AS_STRING;
     */
    static #buildNaNConstants() {
        const staticPropName1 = 'NaN';

        const staticPropName2 = 'NaN_AS_STRING';

        EzNumber.addStaticPropertyToClass(
            this,
            staticPropName1,
            Number.NaN);

        EzNumber.addStaticPropertyToClass(
            this,
            staticPropName1.toUpperCase(),
            EzNumber[staticPropName1]);

        EzNumber.addStaticPropertyToClass(
            this,
            staticPropName2,
            staticPropName1);

        EzNumber.addStaticPropertyToClass(
            this,
            staticPropName2.toUpperCase(),
            EzNumber[staticPropName2]);
    }

    /**
     * @static
     * @description
     * EzNumber's Static initialization section
     * Calls internal methods to add additional constant read-only properties (aka getters) to the EzNumber class.
     */
    static {
        // Build String constants for EzNumber
        this.#buildEzNumberStringConstants();

        // Build number constants for EzNumber
        this.#buildEzNumberConstants();

        // Build float/double constants for EzNumber
        this.#buildEzNumberFloatConstants()

        // Build currency constants for EzNumber
        this.#buildCurrencyConstants();

        this.#buildNaNConstants();
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * DO NOT PLACE ANY CODE BELOW THIS SECTION
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
}

globalThis.EzNumber = EzNumber;
